import React, { useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleX,
  Download,
  Loader2,
  RotateCw,
  Settings2,
} from 'lucide-react';
import * as XLSX from 'xlsx';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';

export const ALL_FILTER_VALUE = '__all__';
export const PAGE_SIZE = 100;

export type SortDirection = 'asc' | 'desc';
export type MonthOption = {
  label: string;
  value: string;
};

export type EcmFieldOption = {
  id: string;
  label: string;
};

export const isInteractiveRecordTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest('a, button, input, select, textarea, [role="menuitem"]'));
};

const formatExportValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    return value.map((item) => {
      if (item && typeof item === 'object') {
        return JSON.stringify(item);
      }
      return `${item ?? ''}`;
    }).join(', ');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return value;
};

export const exportEcmDataset = ({
  columns,
  fileName,
  rows,
}: {
  columns: EcmFieldOption[];
  fileName: string;
  rows: any[];
}) => {
  const exportRows = rows.map((row) => {
    return Object.fromEntries(columns.map((column) => [column.label, formatExportValue(row?.[column.id])]));
  });
  const worksheet = XLSX.utils.json_to_sheet(exportRows, {
    header: columns.map((column) => column.label),
  });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'ECM Dashboard');
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

export const normalizeValue = (value: unknown) => `${value || ''}`.trim();
export const normalizeLowerCase = (value: unknown) => normalizeValue(value).toLowerCase();

export const uniqueSortedValues = (values: Array<string | null | undefined>) => {
  return Array.from(
    new Set(
      values
        .map((value) => normalizeValue(value))
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
};

export const padNumber = (value: number) => `${value}`.padStart(2, '0');
export const toMonthKey = (year: number, month: number) => `${year}-${padNumber(month)}`;

export const getCurrentMonthKey = () => {
  const currentDate = new Date();
  return toMonthKey(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1);
};

export const parseMonthKey = (monthKey: string) => {
  const [rawYear, rawMonth] = monthKey.split('-');
  const year = Number(rawYear);
  const month = Number(rawMonth);

  if (Number.isInteger(year) && Number.isInteger(month) && month >= 1 && month <= 12) {
    return { month, year };
  }

  const [fallbackYear, fallbackMonth] = getCurrentMonthKey().split('-');
  return {
    month: Number(fallbackMonth),
    year: Number(fallbackYear),
  };
};

export const parseIsoDate = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }

  return {
    day: Number(match[3]),
    month: Number(match[2]),
    year: Number(match[1]),
  };
};

export const formatMonthLabel = (year: number, month: number) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    timeZone: 'UTC',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
};

export const formatDate = (value: string | null | undefined) => {
  const parsedDate = parseIsoDate(value);
  if (!parsedDate) {
    return '—';
  }

  return `${padNumber(parsedDate.month)}/${padNumber(parsedDate.day)}/${parsedDate.year}`;
};

export const buildMonthOptions = (startValue: string | null | undefined, endValue: string | null | undefined) => {
  const startDate = parseIsoDate(startValue);
  const endDate = parseIsoDate(endValue);
  if (!startDate || !endDate) {
    return [] as MonthOption[];
  }

  const options: MonthOption[] = [];
  let year = endDate.year;
  let month = endDate.month;

  while (year > startDate.year || (year === startDate.year && month >= startDate.month)) {
    options.push({
      label: formatMonthLabel(year, month),
      value: toMonthKey(year, month),
    });

    month -= 1;
    if (month >= 1) {
      continue;
    }

    month = 12;
    year -= 1;
  }

  return options;
};

export const getSelectedMonthLabel = (monthKey: string, monthOptions: MonthOption[]) => {
  const option = monthOptions.find((item) => item.value === monthKey);
  if (option) {
    return option.label;
  }

  const { month, year } = parseMonthKey(monthKey);
  return formatMonthLabel(year, month);
};

export const compareValues = (left: string, right: string, direction: SortDirection) => {
  const comparison = left.localeCompare(right, undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  if (direction === 'asc') {
    return comparison;
  }

  return comparison * -1;
};

export const buildSearchText = (values: Array<string | null | undefined>) => {
  return values
    .map((value) => normalizeLowerCase(value))
    .filter(Boolean)
    .join(' ');
};

export const matchesFilter = (value: string | null | undefined, selectedValue: string) => {
  if (selectedValue === ALL_FILTER_VALUE) {
    return true;
  }

  return normalizeValue(value) === selectedValue;
};

export const getNotesRequired = (acuityValue: string | null | undefined) => {
  const normalizedAcuity = normalizeLowerCase(acuityValue);
  if (!normalizedAcuity) {
    return null;
  }

  if (normalizedAcuity === '1' || normalizedAcuity.includes('low')) {
    return 1;
  }

  if (normalizedAcuity === '4' || normalizedAcuity.includes('high')) {
    return 4;
  }

  if (normalizedAcuity === '2' || normalizedAcuity.includes('moderate') || normalizedAcuity.includes('medium')) {
    return 2;
  }

  const numericAcuity = Number(normalizedAcuity);
  if (numericAcuity === 1) {
    return 1;
  }

  if (numericAcuity === 4) {
    return 4;
  }

  return null;
};

export const countServiceDatesForMonth = (serviceDates: string[] | null | undefined, monthKey: string) => {
  const { month, year } = parseMonthKey(monthKey);
  return (serviceDates || []).filter((serviceDate) => {
    const parsedDate = parseIsoDate(serviceDate);
    if (!parsedDate) {
      return false;
    }

    return parsedDate.year === year && parsedDate.month === month;
  }).length;
};

export const getInitialAssessmentComplianceStatus = (
  censusStart: string | null | undefined,
  assessmentEntryDate: string | null | undefined
) => {
  if (normalizeValue(assessmentEntryDate)) {
    return 'in-compliance';
  }

  const parsedCensusStart = parseIsoDate(censusStart);
  if (!parsedCensusStart) {
    return 'past-due';
  }

  const today = new Date();
  const censusStartTime = Date.UTC(parsedCensusStart.year, parsedCensusStart.month - 1, parsedCensusStart.day);
  const todayTime = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const daysSinceCensusStart = Math.floor((todayTime - censusStartTime) / (1000 * 60 * 60 * 24));

  if (daysSinceCensusStart < 30) {
    return 'approaching-due-date';
  }

  return 'past-due';
};

export const getCarePlanComplianceStatus = (
  censusStart: string | null | undefined,
  carePlanEntryDate: string | null | undefined
) => {
  const firstCarePlanDueDay = 30;
  const firstCarePlanWarningDay = 15;
  const recurringCarePlanCycleDays = 90;
  const parsedCensusStart = parseIsoDate(censusStart);
  if (!parsedCensusStart) {
    return 'past-due';
  }

  const today = new Date();
  const censusStartTime = Date.UTC(parsedCensusStart.year, parsedCensusStart.month - 1, parsedCensusStart.day);
  const todayTime = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const daysSinceCensusStart = Math.floor((todayTime - censusStartTime) / (1000 * 60 * 60 * 24));

  if (!normalizeValue(carePlanEntryDate)) {
    if (daysSinceCensusStart < firstCarePlanWarningDay) {
      return null;
    }

    if (daysSinceCensusStart >= firstCarePlanDueDay) {
      return 'past-due';
    }

    return 'approaching-due-date';
  }

  const parsedCarePlanDate = parseIsoDate(carePlanEntryDate);
  if (!parsedCarePlanDate) {
    return 'past-due';
  }

  const carePlanTime = Date.UTC(parsedCarePlanDate.year, parsedCarePlanDate.month - 1, parsedCarePlanDate.day);
  const daysSinceLastCarePlan = Math.floor((todayTime - carePlanTime) / (1000 * 60 * 60 * 24));
  const currentCensusCycleDay = (
    daysSinceCensusStart < firstCarePlanDueDay
      ? daysSinceCensusStart
      : (daysSinceCensusStart - firstCarePlanDueDay) % recurringCarePlanCycleDays
  ) + 1;
  const isApproachingDueDate = currentCensusCycleDay >= 60;
  const completedThisCycle = daysSinceLastCarePlan < currentCensusCycleDay;
  const completedLastCycle = daysSinceLastCarePlan < currentCensusCycleDay + recurringCarePlanCycleDays;

  if (daysSinceCensusStart < firstCarePlanDueDay) {
    return 'in-compliance';
  }

  if (!completedThisCycle && !completedLastCycle) {
    return 'past-due';
  }

  if (isApproachingDueDate && !completedThisCycle) {
    return 'approaching-due-date';
  }

  return 'in-compliance';
};

export const getProgramCompletionCompliance = (documentDate: string | null | undefined) => {
  const parsedDate = parseIsoDate(documentDate);
  if (!parsedDate) {
    return false;
  }

  const today = new Date();
  const documentTime = Date.UTC(parsedDate.year, parsedDate.month - 1, parsedDate.day);
  const todayTime = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const daysPassed = Math.floor((todayTime - documentTime) / (1000 * 60 * 60 * 24));
  return daysPassed <= 365;
};

export const getMrunStatus = (mrunValue: string | null | undefined) => {
  const normalizedValue = normalizeValue(mrunValue);
  if (!normalizedValue || normalizedValue.toUpperCase() === 'NULL') {
    return 'NULL';
  }

  const parts = normalizedValue.split(' - ');
  if (parts.length > 1) {
    return normalizeValue(parts[1]) || 'NULL';
  }

  return 'NULL';
};

export const calculateOutreachAttempts = (
  outreachAttempts: Array<{ outreachDate?: string | null; outreachResult?: number | null }> | null | undefined,
  outreachStart: string | null | undefined
) => {
  const attempts = [...(outreachAttempts || [])]
    .filter((attempt) => normalizeValue(attempt?.outreachDate))
    .sort((left, right) => normalizeValue(left?.outreachDate).localeCompare(normalizeValue(right?.outreachDate)))
    .slice(0, 3);

  const result = {
    attempts: [
      { attemptDate: attempts[0]?.outreachDate || null, nextAttemptDate: null as string | null, result: attempts[0]?.outreachResult ?? null, status: 'pending' as string | null },
      { attemptDate: attempts[1]?.outreachDate || null, nextAttemptDate: null as string | null, result: attempts[1]?.outreachResult ?? null, status: null as string | null },
      { attemptDate: attempts[2]?.outreachDate || null, nextAttemptDate: null as string | null, result: attempts[2]?.outreachResult ?? null, status: null as string | null },
    ],
    nextAttemptDate: null as string | null,
    status: 'pending',
  };

  const addDays = (dateValue: string | null | undefined, days: number) => {
    const parsedDate = parseIsoDate(dateValue);
    if (!parsedDate) {
      return null;
    }

    const currentDate = new Date(Date.UTC(parsedDate.year, parsedDate.month - 1, parsedDate.day));
    currentDate.setUTCDate(currentDate.getUTCDate() + days);
    return currentDate.toISOString().slice(0, 10);
  };

  const isPast = (dateValue: string | null | undefined) => {
    const parsedDate = parseIsoDate(dateValue);
    if (!parsedDate) {
      return false;
    }

    const currentDate = new Date();
    const targetTime = Date.UTC(parsedDate.year, parsedDate.month - 1, parsedDate.day);
    const todayTime = Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate());
    return todayTime > targetTime;
  };

  const firstAttempt = attempts[0];
  if (firstAttempt?.outreachResult === 1) {
    result.attempts[0].status = 'enrolled';
    result.status = 'enrolled';
    return result;
  }

  if (firstAttempt?.outreachResult === 0) {
    result.attempts[0].status = 'rejected';
  }

  if (!firstAttempt?.outreachResult && normalizeValue(outreachStart)) {
    const nextAttemptDate = addDays(outreachStart, 7);
    result.nextAttemptDate = nextAttemptDate;
    result.attempts[0].nextAttemptDate = nextAttemptDate;
    const status = isPast(nextAttemptDate) ? 'overdue' : 'pending';
    result.attempts[0].status = status;
    result.status = status;
  }

  const secondAttempt = attempts[1];
  if (secondAttempt?.outreachResult === 1) {
    result.attempts[1].status = 'enrolled';
    result.status = 'enrolled';
    return result;
  }

  if (secondAttempt?.outreachResult === 0) {
    result.attempts[1].status = 'rejected';
  }

  if (!secondAttempt?.outreachResult && firstAttempt) {
    const nextAttemptDate = addDays(firstAttempt.outreachDate, 10);
    result.nextAttemptDate = nextAttemptDate;
    result.attempts[1].nextAttemptDate = nextAttemptDate;
    const status = isPast(nextAttemptDate) ? 'overdue' : 'pending';
    result.attempts[1].status = status;
    result.status = status;
  }

  const thirdAttempt = attempts[2];
  if (thirdAttempt?.outreachResult === 1) {
    result.attempts[2].status = 'enrolled';
    result.status = 'enrolled';
    return result;
  }

  if (thirdAttempt?.outreachResult === 0) {
    result.attempts[2].status = 'rejected';
    result.status = 'rejected';
    return result;
  }

  if (!thirdAttempt?.outreachResult && secondAttempt) {
    const nextAttemptDate = addDays(secondAttempt.outreachDate, 10);
    result.nextAttemptDate = nextAttemptDate;
    result.attempts[2].nextAttemptDate = nextAttemptDate;
    const status = isPast(nextAttemptDate) ? 'overdue' : 'pending';
    result.attempts[2].status = status;
    result.status = status;
  }

  return result;
};

export const getDaysDifference = (startValue: string | null | undefined, endValue: string | null | undefined) => {
  const startDate = parseIsoDate(startValue);
  const endDate = parseIsoDate(endValue);
  if (!startDate || !endDate) {
    return null;
  }

  const startTime = Date.UTC(startDate.year, startDate.month - 1, startDate.day);
  const endTime = Date.UTC(endDate.year, endDate.month - 1, endDate.day);
  return Math.floor((endTime - startTime) / (1000 * 60 * 60 * 24));
};

export const FilterLabel = ({ children }: { children: React.ReactNode }) => {
  return <div className="text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">{children}</div>;
};

export const SortableHeader = ({
  field,
  label,
  onSort,
  sortDirection,
  sortField,
}: {
  field: string;
  label: string;
  onSort: (field: string) => void;
  sortDirection: SortDirection;
  sortField: string;
}) => {
  const isActive = sortField === field;
  const isAscending = isActive && sortDirection === 'asc';
  const isDescending = isActive && sortDirection === 'desc';
  const nextDirection = isAscending ? 'descending' : 'ascending';

  return (
    <button
      aria-label={`Sort by ${label} ${nextDirection}`}
      type="button"
      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.04em] text-slate-700 hover:text-slate-900"
      onClick={() => onSort(field)}
    >
      <span>{label}</span>
      <span aria-hidden="true" className="inline-flex items-center gap-0.5">
        <ArrowUp
          className={`h-3 w-3 ${isAscending ? 'text-[var(--azure-primary)]' : 'text-slate-400'}`}
          data-testid={`ecm-sort-${field}-ascending`}
        />
        <ArrowDown
          className={`h-3 w-3 ${isDescending ? 'text-[var(--azure-primary)]' : 'text-slate-400'}`}
          data-testid={`ecm-sort-${field}-descending`}
        />
      </span>
    </button>
  );
};

export const EcmExportButton = ({ onExport }: { onExport: () => void }) => {
  return (
    <Button
      aria-label="Export entire dataset"
      className="h-8 w-8 shrink-0 p-0"
      size="sm"
      title="Export"
      variant="outline"
      onClick={onExport}
    >
      <Download className="h-3.5 w-3.5" />
    </Button>
  );
};

export const EcmResetButton = ({ onReset }: { onReset: () => void }) => {
  return (
    <Button
      aria-label="Reset filters"
      className="h-8 w-8 p-0"
      size="sm"
      title="Reset filters"
      variant="outline"
      onClick={onReset}
    >
      <RotateCw className="h-3.5 w-3.5" />
    </Button>
  );
};

export const EcmRecordActionButton = ({
  disabled = false,
  icon,
  label,
  onClick,
  separatorBefore = false,
  variant = 'outline',
}: {
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  separatorBefore?: boolean;
  variant?: 'default' | 'destructive' | 'outline';
}) => {
  const button = (
    <Button
      aria-label={label}
      className="h-8 w-8 shrink-0 p-0"
      disabled={disabled}
      size="sm"
      title={label}
      type="button"
      variant={variant}
      onClick={onClick}
    >
      {icon}
    </Button>
  );

  if (separatorBefore) {
    return <div className="ml-1 shrink-0 border-l border-slate-300 pl-2">{button}</div>;
  }

  return button;
};

const formatRecordDetailValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    return value.map((item) => formatExportValue(item)).filter((item) => item !== '').join(', ') || '—';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return `${value}`;
};

export const EcmRecordDetailsDialog = ({
  actions,
  columns,
  item,
  onOpenChange,
  title,
}: {
  actions?: React.ReactNode;
  columns: EcmFieldOption[];
  item: any | null;
  onOpenChange: (open: boolean) => void;
  title: string;
}) => {
  return (
    <Dialog open={Boolean(item)} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-hidden p-0"
        contentHeight="auto"
        contentMaxHeight="90vh"
        contentMaxWidth="95vw"
        contentWidth="72rem"
      >
        <DialogHeader className="border-b border-[var(--neutral-gray-30)] px-4 py-4 pr-12">
          <div className="flex min-h-8 items-center justify-between gap-4">
            <DialogTitle>{title}</DialogTitle>
            {actions ? <div aria-label="Record actions" className="flex items-center gap-2" role="toolbar">{actions}</div> : null}
          </div>
          <DialogDescription className="sr-only">Record details</DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[72vh] grid-cols-1 gap-x-6 gap-y-4 overflow-y-auto px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          {columns.map((column) => (
            <div className="min-w-0" key={column.id}>
              <div className="text-xs font-semibold uppercase tracking-[0.04em] text-[var(--neutral-gray-80)]">{column.label}</div>
              <div className="mt-1 whitespace-pre-wrap break-words text-sm text-[var(--neutral-gray-120)]">
                {formatRecordDetailValue(item?.[column.id])}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TextCell = ({
  className = '',
  value,
}: {
  className?: string;
  value: string | null | undefined;
}) => {
  const displayValue = normalizeValue(value) || '—';

  return (
    <span className={`block truncate ${className}`} title={displayValue}>
      {displayValue}
    </span>
  );
};

export const MissingValue = () => {
  return <CircleX className="h-4 w-4 text-red-700" />;
};

export const StatusIcon = ({ status }: { status: string | boolean | null | undefined }) => {
  if (status === true || status === 'in-compliance' || status === 'enrolled' || status === 'locked') {
    return <CircleCheck className="h-4 w-4 text-emerald-600" />;
  }

  if (status === 'approaching-due-date' || status === 'pending') {
    return <CircleAlert className="h-4 w-4 text-amber-500" />;
  }

  if (status === 'overdue') {
    return <CircleAlert className="h-4 w-4 text-orange-600" />;
  }

  if (status === false || status === 'past-due' || status === 'rejected') {
    return <CircleX className="h-4 w-4 text-red-700" />;
  }

  return <span className="text-slate-400">—</span>;
};

export const PagerControls = ({
  page,
  totalCount,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) => {
  const visibleStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const visibleEnd = totalCount === 0 ? 0 : Math.min(totalCount, page * PAGE_SIZE);

  return (
    <div className="flex items-center gap-2">
      <span>
        Showing {visibleStart} to {visibleEnd} of {totalCount}
      </span>
      <Button aria-label="Previous page" className="h-8 px-2" size="sm" variant="outline" disabled={page <= 1} onClick={() => onPageChange(Math.max(1, page - 1))}>
        <ChevronLeft className="h-3.5 w-3.5" />
      </Button>
      <span>
        Page {Math.min(page, totalPages)} of {totalPages}
      </span>
      <Button
        aria-label="Next page"
        className="h-8 px-2"
        size="sm"
        variant="outline"
        disabled={page >= totalPages}
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

export const resolveVisibleColumnIds = (savedColumnIds: string[] | undefined, defaultColumnIds: string[]) => {
  const validDefaultColumnIds = defaultColumnIds.filter(Boolean);
  if (!Array.isArray(savedColumnIds) || savedColumnIds.length === 0) {
    return validDefaultColumnIds;
  }

  const normalizedSavedColumnSet = new Set(savedColumnIds.filter((columnId) => validDefaultColumnIds.includes(columnId)));
  const normalizedSavedColumnIds = validDefaultColumnIds.filter((columnId) => normalizedSavedColumnSet.has(columnId));
  if (normalizedSavedColumnIds.length === 0) {
    return validDefaultColumnIds;
  }

  return normalizedSavedColumnIds;
};

export const buildVisibleColumnSet = (visibleColumnIds: string[]) => {
  return new Set(visibleColumnIds);
};

export const EcmFieldSettingsMenu = ({
  columns,
  onVisibleColumnsChange,
  visibleColumnIds,
}: {
  columns: EcmFieldOption[];
  onVisibleColumnsChange: (visibleColumnIds: string[]) => void;
  visibleColumnIds: string[];
}) => {
  const visibleColumnSet = buildVisibleColumnSet(visibleColumnIds);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button aria-label="Choose visible fields" className="h-8 w-8 p-0" size="sm" title="Fields" variant="outline">
          <Settings2 className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>Show or Hide Fields</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {columns.map((column) => {
          const isChecked = visibleColumnSet.has(column.id);
          const isLastVisibleColumn = isChecked && visibleColumnIds.length === 1;

          return (
            <DropdownMenuCheckboxItem
              key={column.id}
              checked={isChecked}
              disabled={isLastVisibleColumn}
              onCheckedChange={(checked) => {
                if (checked) {
                  const nextColumnIds = resolveVisibleColumnIds([...visibleColumnIds, column.id], columns.map((item) => item.id));
                  onVisibleColumnsChange(nextColumnIds);
                  return;
                }

                if (isLastVisibleColumn) {
                  return;
                }

                onVisibleColumnsChange(visibleColumnIds.filter((columnId) => columnId !== column.id));
              }}
            >
              {column.label}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export type EcmSearchClient = {
  cid?: string | null;
  cin?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  odrparticipant?: number | string | null;
};

type EcmParticipantSearchResult = {
  cid?: string | null;
  cin?: string | null;
  id?: number | string | null;
  name?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null;
};

export const ECM_PARTICIPANT_SEARCH_QUERY = gql`
  query EcmParticipantSearch($search: String!) {
    searchParticipants(search: $search) {
      id
      name {
        firstName
        lastName
      }
      cid
      cin
    }
  }
`;

export const ECM_UPDATE_DETERMINATION_MUTATION = gql`
  mutation UpdateEcmDetermination($determination: String!, $odrparticipant: Int!) {
    updateEcmDetermination(determination: $determination, odrparticipant: $odrparticipant) {
      determination
      odrparticipant
      success
    }
  }
`;

export const ECM_REMOVE_CENSUS_PARTICIPANT_MUTATION = gql`
  mutation RemoveEcmCensusParticipant($odrparticipant: Int!) {
    removeEcmCensusParticipant(odrparticipant: $odrparticipant) {
      odrparticipant
      success
    }
  }
`;

export const ECM_ADD_OUTREACH_PARTICIPANT_MUTATION = gql`
  mutation AddEcmOutreachParticipant($odrparticipant: Int!) {
    addEcmOutreachParticipant(odrparticipant: $odrparticipant) {
      alreadyActive
      odrparticipant
      success
    }
  }
`;

export const ECM_REMOVE_OUTREACH_PARTICIPANT_MUTATION = gql`
  mutation RemoveEcmOutreachParticipant($odrparticipant: Int!) {
    removeEcmOutreachParticipant(odrparticipant: $odrparticipant) {
      odrparticipant
      success
    }
  }
`;

export const ECM_MOVE_OUTREACH_PARTICIPANT_MUTATION = gql`
  mutation MoveEcmOutreachParticipant($destination: String!, $odrparticipant: Int!) {
    moveEcmOutreachParticipant(destination: $destination, odrparticipant: $odrparticipant) {
      odrparticipant
      success
    }
  }
`;

export const ECM_MOVE_CENSUS_PARTICIPANT_TO_OUTREACH_MUTATION = gql`
  mutation MoveEcmCensusParticipantToOutreach($odrparticipant: Int!) {
    moveEcmCensusParticipantToOutreach(odrparticipant: $odrparticipant) {
      odrparticipant
      success
    }
  }
`;

export const ECM_REQUEST_DEACTIVATION_MUTATION = gql`
  mutation RequestEcmDeactivation($input: EcmDeactivationRequestInput!, $odrparticipant: Int!) {
    requestEcmDeactivation(input: $input, odrparticipant: $odrparticipant) {
      odrparticipant
      success
    }
  }
`;

export const EcmAddParticipantDialog = ({
  closeLabel = 'Cancel',
  description,
  open,
  onOpenChange,
  onSubmit,
  submitLabel,
  title,
}: {
  closeLabel?: string;
  description: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: { odrparticipant: number }) => Promise<void>;
  submitLabel: string;
  title: string;
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<EcmSearchClient[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedClient, setSelectedClient] = useState<EcmSearchClient | null>(null);
  const [searchParticipants] = useLazyQuery(ECM_PARTICIPANT_SEARCH_QUERY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!open) {
      setIsSearching(false);
      setResults([]);
      setSearchError(null);
      setSelectedClient(null);
      setQuery('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const normalizedQuery = normalizeValue(query);
    if (normalizedQuery.length < 3) {
      setResults([]);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setIsSearching(true);
      setSearchError(null);

      void searchParticipants({ variables: { search: normalizedQuery } })
        .then((response) => {
          if (cancelled) {
            return;
          }

          const participants = Array.isArray(response.data?.searchParticipants)
            ? response.data.searchParticipants as EcmParticipantSearchResult[]
            : [];
          setResults(participants.map((participant) => ({
            cid: participant.cid,
            cin: participant.cin,
            firstname: participant.name?.firstName,
            lastname: participant.name?.lastName,
            odrparticipant: participant.id,
          })));
        })
        .catch((error: unknown) => {
          if (cancelled) {
            return;
          }

          const errorMessage = error instanceof Error ? error.message : 'Unable to search participants.';
          setResults([]);
          if (normalizeLowerCase(errorMessage).includes('no participant found')) {
            setSearchError(null);
            return;
          }
          setSearchError(errorMessage);
        })
        .finally(() => {
          if (cancelled) {
            return;
          }

          setIsSearching(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [open, query, searchParticipants]);

  const handleSubmit = async () => {
    if (!selectedClient || isSubmitting) {
      return;
    }

    const odrparticipant = Number(selectedClient.odrparticipant);
    if (!Number.isInteger(odrparticipant) || odrparticipant <= 0) {
      setSearchError('Selected participant is missing a valid ODR participant number.');
      return;
    }

    setIsSubmitting(true);
    setSearchError(null);
    try {
      await onSubmit({ odrparticipant });
      onOpenChange(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unable to submit request.';
      setSearchError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeButton = (
    <Button
      variant="outline"
      onClick={() => {
        onOpenChange(false);
      }}
    >
      {closeLabel}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent contentHeight="auto" contentMaxHeight="80vh" contentMaxWidth="95vw" contentWidth="42rem">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <FilterLabel>Search Participant</FilterLabel>
            <Input
              className="h-9"
              onChange={(event) => {
                setQuery(event.target.value);
                setSelectedClient(null);
              }}
              placeholder="Search by name, CID, or CIN"
              value={query}
            />
          </div>
          <div className="rounded-md border border-slate-200">
            {normalizeValue(query).length < 3 ? (
              <div className="px-3 py-4 text-sm text-slate-500">Type at least 3 characters to search.</div>
            ) : null}
            {normalizeValue(query).length >= 3 && isSearching ? (
              <div className="flex items-center gap-2 px-3 py-4 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching participants...
              </div>
            ) : null}
            {normalizeValue(query).length >= 3 && !isSearching && results.length === 0 && !searchError ? (
              <div className="px-3 py-4 text-sm text-slate-500">No matching participants found.</div>
            ) : null}
            {results.length > 0 ? (
              <div className="max-h-64 space-y-1 overflow-auto p-2">
                {results.map((client) => {
                  const key = `${client.odrparticipant || 'none'}-${client.cin || 'none'}-${client.cid || 'none'}`;
                  const fullName = [normalizeValue(client.firstname), normalizeValue(client.lastname)].filter(Boolean).join(' ') || 'Unknown Name';
                  const detailText = `CIN: ${normalizeValue(client.cin) || 'NONE'} • CID: ${normalizeValue(client.cid) || 'NONE'}`;
                  const isSelected = selectedClient?.odrparticipant === client.odrparticipant;

                  return (
                    <button
                      key={key}
                      type="button"
                      className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                        isSelected ? 'border-sky-300 bg-sky-50 text-sky-900' : 'border-slate-200 hover:bg-slate-50'
                      }`}
                      onClick={() => setSelectedClient(client)}
                    >
                      <div className="font-medium">{fullName}</div>
                      <div className="text-xs text-slate-500">{detailText}</div>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          {searchError ? <div className="text-sm text-red-700">{searchError}</div> : null}
          <div className="flex justify-end gap-2">
            <Button disabled={!selectedClient || isSubmitting} onClick={() => void handleSubmit()}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitLabel}
            </Button>
            {closeButton}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
